import { useState } from 'react';

import { BodyMedium, BodyXS, Box, Modal, ModalFooter, ModalHeader, TextField, VisibilityIcon, VisibilityOffIcon } from '@parspec/pixel';

import { INVALID_PASSWORD_MSG, PASSWORD_MISMATCH_MSG, PASSWORD_SAVED_MSG, REQUIRED_FIELD } from 'src/features/shared/constants';
import { useEditUserProfileInfoMutation, useGetUserProfileInfoQuery } from '../../queries';
import { CHANGE_PASSWORD_FORM_INIT_STATE, initialErrorState, newPasswordVerifier, PASSWORD_FIELDS } from '../../utils/utils';

interface IChangePasswordProps {
    open: boolean;
    onClose: () => void;
    handleSnackbarMsg: (msg: string) => void;
}

const ChangePasswordModal = ({ open, onClose, handleSnackbarMsg }: IChangePasswordProps) => {
    const [errorState, setErrorState] = useState(initialErrorState);
    const [profileState, setProfileState] = useState(CHANGE_PASSWORD_FORM_INIT_STATE);
    const { data: userProfile } = useGetUserProfileInfoQuery();

    const { mutateAsync: editUserDetailsMutation, isLoading: editUserDetailsLoading } = useEditUserProfileInfoMutation();

    const togglePasswordVisibility = (passwordFieldName: keyof typeof profileState) => {
        setProfileState(() => ({ ...profileState, [passwordFieldName]: { ...profileState[passwordFieldName], showPassword: !profileState[passwordFieldName].showPassword } }));
    };

    const handleErrorMessage = (fieldName: keyof typeof profileState, errorMsg: string) => {
        setErrorState({
            ...errorState,
            [fieldName]: { ...errorState[fieldName], isError: true, title: errorMsg }
        });
    };

    const fieldChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
        const fieldName = event.target?.name as keyof typeof profileState;
        const fieldValue = event.target?.value;

        if (!Object.prototype.hasOwnProperty.call(errorState, fieldName) && !Object.prototype.hasOwnProperty.call(profileState, fieldName)) {
            return;
        }

        if (errorState[fieldName].isError) {
            setErrorState({ ...errorState, [fieldName]: { ...errorState[fieldName], isError: false, title: '' } });
        }

        if (fieldName === PASSWORD_FIELDS.CURRENT_PASSWORD && !fieldValue.trim()) {
            handleErrorMessage(PASSWORD_FIELDS.CURRENT_PASSWORD, REQUIRED_FIELD);
        }
        setProfileState(() => ({ ...profileState, [fieldName]: { ...profileState[fieldName], data: fieldValue } }));
    };

    const fieldBlurHandler = (event: React.FocusEvent<HTMLInputElement>) => {
        const fieldName = event.target?.name as keyof typeof profileState;
        const eventType = event.type;

        if (!Object.prototype.hasOwnProperty.call(errorState, fieldName) && !Object.prototype.hasOwnProperty.call(profileState, fieldName)) {
            return;
        }

        if (!profileState[fieldName].data.trim() && eventType === 'blur') {
            return handleErrorMessage(fieldName, REQUIRED_FIELD);
        }
    };

    const handleNewPasswordBlur = () => {
        if (!profileState.newPassword.data.trim()) {
            return handleErrorMessage(PASSWORD_FIELDS.NEW_PASSWORD as keyof typeof profileState, REQUIRED_FIELD);
        }
        if (!newPasswordVerifier(profileState.newPassword.data)) {
            return handleErrorMessage(PASSWORD_FIELDS.NEW_PASSWORD as keyof typeof profileState, INVALID_PASSWORD_MSG);
        }
    };

    const handleConfirmPasswordBlur = () => {
        if (!profileState.confirmPassword.data.trim()) {
            return handleErrorMessage(PASSWORD_FIELDS.CONFIRM_PASSWORD as keyof typeof profileState, REQUIRED_FIELD);
        }
        if (profileState.newPassword.data !== profileState.confirmPassword.data) {
            return handleErrorMessage(PASSWORD_FIELDS.CONFIRM_PASSWORD as keyof typeof profileState, PASSWORD_MISMATCH_MSG);
        }
    };

    const onSubmitHandler = async () => {
        const currentPasswordFlag = !profileState.currentPassword.data.trim();
        const newPasswordFlag = !profileState.newPassword.data.trim();
        const confirmPasswordFlag = !profileState.confirmPassword.data.trim();

        if (newPasswordFlag || confirmPasswordFlag || currentPasswordFlag) {
            return setErrorState({
                ...errorState,
                currentPassword: { ...errorState.currentPassword, isError: currentPasswordFlag, title: `${currentPasswordFlag ? REQUIRED_FIELD : ''}` },
                newPassword: { ...errorState.newPassword, isError: newPasswordFlag, title: `${newPasswordFlag ? REQUIRED_FIELD : ''}` },
                confirmPassword: { ...errorState.confirmPassword, isError: confirmPasswordFlag, title: `${confirmPasswordFlag ? REQUIRED_FIELD : ''}` }
            });
        }

        if (profileState.newPassword.data.trim() && !newPasswordVerifier(profileState.newPassword.data)) {
            return handleErrorMessage(PASSWORD_FIELDS.NEW_PASSWORD as keyof typeof profileState, INVALID_PASSWORD_MSG);
        }
        if (profileState.newPassword.data.trim() && profileState.confirmPassword.data.trim() && profileState.newPassword.data !== profileState.confirmPassword.data) {
            return handleErrorMessage(PASSWORD_FIELDS.CONFIRM_PASSWORD as keyof typeof profileState, PASSWORD_MISMATCH_MSG);
        }
        try {
            const payload = {
                password: profileState.currentPassword.data,
                new_password: profileState.confirmPassword.data,
                confirm_password: profileState.confirmPassword.data,
                first_name: userProfile?.data?.first_name, last_name: userProfile?.data?.last_name, email: userProfile?.data?.email
            };
            await editUserDetailsMutation(payload);
            handleSnackbarMsg(PASSWORD_SAVED_MSG);
            onClose();
        } catch (e: any) {
            if (e.response.status === 400) {
                return handleErrorMessage(PASSWORD_FIELDS.CURRENT_PASSWORD as keyof typeof profileState, e?.response?.data?.message || "Invalid password.")
            }
            if (e.response.status === 417) {
                return handleErrorMessage(PASSWORD_FIELDS.NEW_PASSWORD as keyof typeof profileState, e?.response?.data?.message || "New password can’t be the same as old password.")
            }
        }
    };

    const keydownHandler = async (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === 'Enter') {
            event.preventDefault();
            onSubmitHandler();
        }
    };

    const getTextFieldIcon = (fieldName: keyof typeof profileState) => {
        return profileState[fieldName].showPassword ? (
            <VisibilityOffIcon
                cursor={'pointer'}
                onClick={() => {
                    togglePasswordVisibility(fieldName);
                }}
            />
        ) : (
            <VisibilityIcon
                cursor={'pointer'}
                onClick={() => {
                    togglePasswordVisibility(fieldName);
                }}
            />
        );
    };

    return (
        <Modal
            open={open}
            header={<ModalHeader title="Changing Password" onClose={onClose} />}
            footer={<ModalFooter onAccept={onSubmitHandler} onReject={onClose} continueButtonLabel="Save" isLoading={editUserDetailsLoading} />}
        >
            <Box width={600} display="flex" gap={4} py={3}>
                <Box width="50%">
                    <form autoComplete={'off'} style={{ height: '100%' }}>
                        <Box height="100%" display="flex" flexDirection="column">
                            <Box>
                                <TextField
                                    id={'currentPassword'}
                                    name={PASSWORD_FIELDS.CURRENT_PASSWORD}
                                    autoComplete={'new-password'}
                                    label={'Current Password'}
                                    required
                                    type={profileState.currentPassword.showPassword ? 'text' : 'password'}
                                    value={profileState.currentPassword.data}
                                    error={errorState.currentPassword.isError}
                                    helperText={errorState.currentPassword.title}
                                    onKeyDown={keydownHandler}
                                    onBlur={fieldBlurHandler}
                                    onChange={fieldChangeHandler}
                                    endIcon={getTextFieldIcon(PASSWORD_FIELDS.CURRENT_PASSWORD as keyof typeof profileState)}
                                />
                            </Box>
                            <Box mt={4}>
                                <TextField
                                    id={'newPassword'}
                                    name={'newPassword'}
                                    autoComplete={'new-password-2'}
                                    label={'New Password'}
                                    required
                                    type={profileState.newPassword.showPassword ? 'text' : 'password'}
                                    value={profileState.newPassword.data}
                                    error={errorState.newPassword.isError}
                                    helperText={errorState.newPassword.title}
                                    onKeyDown={keydownHandler}
                                    onChange={fieldChangeHandler}
                                    onBlur={handleNewPasswordBlur}
                                    endIcon={getTextFieldIcon(PASSWORD_FIELDS.NEW_PASSWORD as keyof typeof profileState)}
                                />
                            </Box>
                            <Box mt={4}>
                                <TextField
                                    id={'confirmPassword'}
                                    name={'confirmPassword'}
                                    autoComplete={'new-password-3'}
                                    label={'Confirm New Password'}
                                    required
                                    type={profileState.confirmPassword.showPassword ? 'text' : 'password'}
                                    value={profileState.confirmPassword.data}
                                    error={errorState.confirmPassword.isError}
                                    helperText={errorState.confirmPassword.title}
                                    onKeyDown={keydownHandler}
                                    onBlur={handleConfirmPasswordBlur}
                                    onChange={fieldChangeHandler}
                                    endIcon={getTextFieldIcon(PASSWORD_FIELDS.CONFIRM_PASSWORD as keyof typeof profileState)}
                                />
                            </Box>
                        </Box>
                    </form>
                </Box>
                <Box width="50%" height={168} bgcolor="neutral.light">
                    <BodyMedium p={2}>Password Requirements</BodyMedium>
                    <BodyXS color="text.secondary" p={2} limit={false}>
                        1. At least 8 characters
                    </BodyXS>
                    <BodyXS color="text.secondary" p={2} limit={false}>
                        2. At least one uppercase letter
                    </BodyXS>
                    <BodyXS color="text.secondary" p={2} limit={false}>
                        3. At least one lowercase letter
                    </BodyXS>
                    <BodyXS color="text.secondary" p={2} limit={false}>
                        4. At least one number
                    </BodyXS>
                </Box>
            </Box>
        </Modal>
    );
};

export default ChangePasswordModal;
